import { useEffect, useState } from 'react';
import { api, fmtCRC, fmtDate } from '../api';
import { useWhatsAppChannel } from '../hooks/useWhatsAppChannel';
import { IconChat, IconCheck, IconSend, IconSettings, IconZap } from '../components/Icons';

export default function Reminders() {
  const { isPro, connected, instant } = useWhatsAppChannel();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [sent, setSent] = useState({});
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState({ text: '', ok: true });

  useEffect(() => {
    api(`/reminders?days=${days}`).then(setData).catch(() => {});
  }, [days]);

  const markSent = (id) => setSent((s) => ({ ...s, [id]: true }));

  const sendInstant = async (item) => {
    setBusy(item.subscriptionId);
    setNotice({ text: '', ok: true });
    try {
      await api(`/whatsapp/subscriptions/${item.subscriptionId}/reminder`, {
        method: 'POST',
        body: { type: item.status === 'vencida' ? 'overdue' : 'reminder' },
      });
      markSent(item.subscriptionId);
      setNotice({ text: `Mensaje enviado a ${item.client.name}.`, ok: true });
    } catch (error) {
      setNotice({ text: error.message, ok: false });
    } finally {
      setBusy('');
    }
  };

  const runAutomation = async () => {
    if (!confirm('Se enviarán solo los mensajes que correspondan hoy según tus reglas. ¿Continuar?')) return;
    setBusy('all');
    try {
      const result = await api('/whatsapp/automation/run', { method: 'POST' });
      setNotice({ text: `${result.sent} enviados, ${result.skipped} omitidos y ${result.failed} fallidos.`, ok: result.failed === 0 });
    } catch (error) {
      setNotice({ text: error.message, ok: false });
    } finally {
      setBusy('');
    }
  };

  if (!data) return <p className="text-slate-400">Cargando…</p>;

  const expired = data.items.filter((i) => i.status === 'vencida');
  const upcoming = data.items.filter((i) => i.status === 'por_vencer');

  const Item = ({ item }) => (
    <div className={`card flex flex-wrap items-center gap-3 ${sent[item.subscriptionId] ? 'opacity-50' : ''}`}>
      <div className="min-w-0 flex-1">
        <div className="font-medium">{item.client.name}</div>
        <div className="text-sm text-slate-400">
          {item.service} · {fmtCRC(item.price)} · vence {fmtDate(item.nextDueDate)}
          {item.daysLeft < 0
            ? <span className="ml-1 text-red-400">(hace {-item.daysLeft} día{-item.daysLeft !== 1 && 's'})</span>
            : item.daysLeft === 0
              ? <span className="ml-1 text-amber-400">(¡hoy!)</span>
              : <span className="ml-1 text-amber-400">(en {item.daysLeft} día{item.daysLeft !== 1 && 's'})</span>}
        </div>
      </div>
      {instant ? (
        <button
          type="button"
          onClick={() => sendInstant(item)}
          disabled={!!busy || sent[item.subscriptionId]}
          className="btn bg-emerald-600 text-white hover:bg-emerald-500"
        >
          {sent[item.subscriptionId] ? <IconCheck size={16} /> : <IconSend size={16} />}
          {sent[item.subscriptionId] ? 'Enviado' : busy === item.subscriptionId ? 'Enviando…' : 'Enviar ahora'}
        </button>
      ) : (
        <a
          href={item.waLink}
          target="_blank"
          rel="noreferrer"
          onClick={() => markSent(item.subscriptionId)}
          className="btn bg-emerald-600 text-white hover:bg-emerald-500"
        >
          <IconChat size={16} /> Abrir WhatsApp
        </a>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Recordatorios de cobro</h1>
        <div className="flex flex-wrap items-center gap-2">
          {isPro && <button className="btn-primary" onClick={runAutomation} disabled={!connected || !!busy}><IconZap size={16} /> {busy === 'all' ? 'Procesando…' : 'Ejecutar reglas'}</button>}
          <label className="text-sm text-slate-400">
            Ver próximos{' '}
            <select className="input inline-block w-auto" value={days} onChange={(e) => setDays(e.target.value)}>
              <option value="3">3 días</option>
              <option value="7">7 días</option>
              <option value="15">15 días</option>
              <option value="30">30 días</option>
            </select>
          </label>
        </div>
      </div>

      <div className={`reminder-plan-notice ${isPro ? 'is-pro' : 'is-basic'}`}>
        <span>{isPro ? <IconZap size={18} /> : <IconChat size={18} />}</span>
        <div>
          <strong>{isPro ? (connected ? 'WhatsApp Pro conectado' : 'Conecta WhatsApp para enviar al instante') : 'Modo Básico · envío asistido'}</strong>
          <p>{isPro ? (connected ? 'Los botones envían directamente desde tu número vinculado.' : 'Vincula tu número en Mensajes automáticos; mientras tanto los botones abren WhatsApp con el mensaje listo.') : 'Cada botón abre WhatsApp Web con el mensaje listo para que confirmes.'}</p>
        </div>
        <a href="/ajustes"><IconSettings size={15} /> Configurar</a>
      </div>
      {notice.text && <p className={notice.ok ? 'settings-success' : 'form-error'}>{notice.text}</p>}

      {expired.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-red-400">Vencidas ({expired.length})</h2>
          {expired.map((i) => <Item key={i.subscriptionId} item={i} />)}
        </section>
      )}

      {upcoming.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-amber-400">Por vencer ({upcoming.length})</h2>
          {upcoming.map((i) => <Item key={i.subscriptionId} item={i} />)}
        </section>
      )}

      {data.items.length === 0 && (
        <div className="card flex items-center justify-center gap-2 text-center text-slate-500">
          <IconCheck size={18} /> Nada pendiente de cobro en los próximos {days} días.
        </div>
      )}
    </div>
  );
}
